async function resilientFetch(url, options) {
  try {
    const response = await window.fetch(url, options);
    return response;
  } catch (error) {
    if (!window.__nativeFetch) throw error;
    const fallbackResponse = await window.__nativeFetch(url, options);
    return fallbackResponse;
  }
}

class PergolaProduct {
  /**
   * Constructor
   *
   * @param {number}  id                   Current product ID
   * @param {string}  title                Current product title
   * @param {Object}  variants             Current product variants
   * @param {number}  price                Current product
   * @param {Object}  options_with_values  Current product options
   * @param {string}  price_symbol         Currency symbol
   * @param {element} total_price_input    Element for the total price text
   * @param {number}  compare_at_price     Current product compare at price
   * @param {Object}  screen_roller_blind  Screen wall external product
   * @param {Object}  wooden_slatted_wall  Wooden wall external product
   * @param {Object}  glass_walls          Glass wall external product
   * @param {Object}  aluminium_walls      Aluminium wall external product
   * @param {Object}  free_accessories     Free accessory external products
   * @param {Object} led_stripes           Led stripes external product
   * @param {Object} custom_product        Custom product to add to cart
   * @param {number} updatePrice           Default: 0, if we should update the total price or not
   * @param {number} mainProduct           If the product is a proper main product or the custom Pergola page
   */
  constructor(
    id,
    title,
    variants,
    price,
    options_with_values,
    price_symbol,
    total_price_input,
    compare_at_price,
    screen_roller_blind,
    wooden_slatted_wall,
    glass_walls,
    aluminium_walls,
    free_accessories,
    led_stripes,
    custom_product,
    updatePrice = 1,
    mainProduct = 0,
    pergolaUnit = 'millimeters',
    productTags = []
  ) {
    this.id = id;
    this.title = title;
    this.variants = variants;
    this.price = price;
    this.options_with_values = options_with_values;
    this.price_symbol = price_symbol;
    this.total_price_input = total_price_input;
    this.compare_at_price = compare_at_price;
    this.pergolaUnit = pergolaUnit;
    this.productTags = productTags;

    this.VERSION_HEIGHT_OFFSETS = {
      's3': 81,
      's4': 81,
    };
    this.DEFAULT_VERSION = 's3';
    this.pergolaVersion = this.getVersionFromTags(productTags);
    if (this.compare_at_price != 0) {
      this.price_difference = price - compare_at_price;
    } else {
      this.price_difference = 0;
    }
    this.screen_walls = screen_roller_blind; //https://admin.shopify.com/store/pergolux-tyskland/products/8452905861387
    this.wood_walls = wooden_slatted_wall; //https://admin.shopify.com/store/pergolux-tyskland/products/8452919034123
    this.glass_walls = glass_walls; //https://admin.shopify.com/store/pergolux-tyskland/products/8452927619339
    this.aluminium_walls = aluminium_walls; //https://admin.shopify.com/store/pergolux-tyskland/products/15213713752439
    this.custom_product = custom_product;
    this.free_accessories = free_accessories || [];
    this.led_stripes = led_stripes;
    this.current_variant = null;
    this.property_inputs = [];
    this.disabledWallValue = 'Keine';
    this.wallProperties = [
      'properties[Linke Seite]',
      'properties[Rechte Seite]',
      'properties[Vorderseite]',
      'properties[Rückseite]',
    ];
    this.walls = {
      width: ['properties[Linke Seite]', 'properties[Rechte Seite]'],
      length: ['properties[Vorderseite]', 'properties[Rückseite]'],
    };

    this.lampWatt = '1500W';
    this.freeAccessoryProperties = ['properties[Zubehör]'];
    this.ledStripePropery = 'properties[Zubehör 2]';
    this.ledStripeProperyRGB = 'properties[Zubehör 3]';
    this.ledStripeVariant = null;
    this.colorProperty = 'Farbe';
    this.colors = {
      white: 'White',
      anthracite: 'Anthracite',
      anthracite_with_white_roof: 'Anthracite with white roof',
    };

    this.update_price = updatePrice;
    this.mainProduct = mainProduct;

    if (this.mainProduct) {
      this.colorSelectorName = 'Color';
    } else {
      this.colorSelectorName = 'properties[Color]';
    }

    this.translations = {};

    this.FORMULA = {
      basePrice: 2921,
      minSqm: 3.0,
      pricePerSqm: 288,

      woodBasePrice: 800,
      woodMinSqm: 2.25,
      woodPricePerSqm: 155,

      screenBasePrice: 770,
      screenMinSqm: 2.25,
      screenPricePerSqm: 72,

      glassBasePrice: 1329,
      glassMinSqm: 2.25,
      glassPricePerSqm: 150,
      
      aluminiumBasePrice: 1190,
      aluminiumMinSqm: 2.25,
      aluminiumPricePerSqm: 125,

      disPr: 1,
      disLr: 1,
      disPl: 0.3,
    };

    this.heightInMeters = null;
    this.lengthInMeters = null;
    this.widthInMeters = null;
    this.sizes = {
      height: null,
      width: null,
      length: null,
      structure_height: null,
      structure_width: null,
      structure_length: null,
      walkthrough_length: null,
      columns_length: null,
      columns_length_feet: null,
      columns_width: null,
      columns_width_feet: null,
    };
    this.extra_sizes = {};
    this.structure_variant = false;
    this.calculatedPrices = {};

    let inputs = document.getElementsByTagName('input');

    for (const i in inputs) {
      if (
        inputs[i].name &&
        inputs[i].name.includes('properties') &&
        !this.property_inputs.includes(inputs[i].name)
      ) {
        this.property_inputs.push(inputs[i].name);
      }
    }

    this.locale = window.Shopify.locale + '-' + window.Shopify.country;
    this.moneyFormat = '${{amount}}';
    this.calculatedFormattedPrices = [];
  }

  /**
   * Set translations from Shopify Liquid
   *
   * @param {JSON}  translations
   */
  setTranslations = (translations) => {
    let self = this;
    self.translations = translations;

    self.disabledWallValue = self.translations.none;

    if (self.mainProduct) {
      self.colorSelectorName = self.translations.color;
    } else {
      self.colorSelectorName = 'properties[' + self.translations.color + ']';
    }

    self.freeAccessoryProperties = self.free_accessories.map((product, index) =>
      index === 0
        ? 'properties[' + self.translations.accessories + ']'
        : 'properties[' + self.translations.accessories + ' ' + (index + 3) + ']'
    );
    self.ledStripePropery = 'properties[' + self.translations.accessories + ' 2]';
    self.ledStripeProperyRGB = 'properties[' + self.translations.accessories + ' 3]';

    if (self.mainProduct) {
      self.colorSelectorName = self.translations.color;
    } else {
      self.colorSelectorName = 'properties[' + self.translations.color + ']';
    }

    self.wallProperties = [
      'properties[' + self.translations.left_side + ']',
      'properties[' + self.translations.right_side + ']',
      'properties[' + self.translations.front_side + ']',
      'properties[' + self.translations.back_side + ']',
      'properties[' + self.translations.front_side + ' ' + self.translations.left + ']',
      'properties[' + self.translations.back_side + ' ' + self.translations.left + ']',
      'properties[' + self.translations.front_side + ' ' + self.translations.right + ']',
      'properties[' + self.translations.back_side + ' ' + self.translations.right + ']',
    ];
    self.walls = {
      width: [
        'properties[' + self.translations.left_side + ']',
        'properties[' + self.translations.right_side + ']',
      ],
      length: [
        'properties[' + self.translations.front_side + ']',
        'properties[' + self.translations.back_side + ']',
        'properties[' + self.translations.front_side + ' ' + self.translations.left + ']',
        'properties[' + self.translations.back_side + ' ' + self.translations.left + ']',
        'properties[' + self.translations.front_side + ' ' + self.translations.right + ']',
        'properties[' + self.translations.back_side + ' ' + self.translations.right + ']',
      ],
    };

    self.lampWatt = self.translations.lamp_watt;

    self.colors = {
      white: self.translations.color_white,
      black: self.translations.color_black,
      anthracite: self.translations.color_anthracite,
      black_with_white: self.translations.color_black_with_white,
      anthracite_with_white_roof: self.translations.color_anthracite_with_white_roof,
    };
  };

  /**
   * Get pergola version from product tags
   * Looks for tags like 'custom-design:s3', 'custom-design:s4', etc.
   *
   * @param {Array} tags - Product tags array
   * @returns {string} - Custom design string (e.g., 's3', 's4') or default version
   */
  getVersionFromTags = (tags) => {
    if (!Array.isArray(tags)) return this.DEFAULT_VERSION;
    const versionTag = tags.find(tag => tag.toLowerCase().startsWith('custom-design:'));

    if (!versionTag) return this.DEFAULT_VERSION;
    const version = versionTag.split(':')[1]?.toLowerCase();

    return this.VERSION_HEIGHT_OFFSETS[version] ? version : this.DEFAULT_VERSION;
  };

  /**
   * Set formula from Shopify Liquid
   *
   * How it's used:
   * base_price: int
   * min_sqm: float
   * price_per_sqm: int
   * wood_base_price: int
   * wood_min_sqm: float
   * wood_price_per_sqm: int
   * screen_base_price: int
   * screen_min_sqm: float
   * screen_price_per_sqm: int
   * glass_base_price: int
   * glass_min_sqm: float
   * glass_price_per_sqm: int
   * dis_pr: int
   * dis_lr: int
   * dis_pl: float
   * multiplier: int
   *
   * @param {*} formula
   */

  setFormula = (formula) => {
    let self = this;
    self.FORMULA = {
      basePrice: parseInt(formula.base_price) * parseFloat(formula.multiplier),
      minSqm: parseFloat(formula.min_sqm),
      pricePerSqm: parseInt(formula.price_per_sqm) * parseFloat(formula.multiplier),

      woodBasePrice: parseInt(formula.wood_base_price) * parseFloat(formula.multiplier),
      woodMinSqm: parseFloat(formula.wood_min_sqm),
      woodPricePerSqm: parseInt(formula.wood_price_per_sqm) * parseFloat(formula.multiplier),

      screenBasePrice: parseInt(formula.screen_base_price) * parseFloat(formula.multiplier),
      screenMinSqm: parseFloat(formula.screen_min_sqm),
      screenPricePerSqm: parseInt(formula.screen_price_per_sqm) * parseFloat(formula.multiplier),

      glassBasePrice: parseInt(formula.glass_base_price) * parseFloat(formula.multiplier),
      glassMinSqm: parseFloat(formula.glass_min_sqm),
      glassPricePerSqm: parseInt(formula.glass_price_per_sqm) * parseFloat(formula.multiplier),
      
      aluminiumBasePrice: parseInt(formula.aluminium_base_price) * parseFloat(formula.multiplier),
      aluminiumMinSqm: parseFloat(formula.aluminium_min_sqm),
      aluminiumPricePerSqm: parseInt(formula.aluminium_price_per_sqm) * parseFloat(formula.multiplier),

      disPr: parseInt(formula.dis_pr),
      disLr: parseInt(formula.dis_lr),
      disPl: parseFloat(formula.dis_pl),
      discount_percent: parseInt(formula.discount_percent),
      discount_text: formula.discount_text,
    };
  };

  /**
   * Add event listeners to elements
   */
  addEventListeners = () => {
    let self = this;
    self.total_price_input.val = 1500;
    let inputs = [
      document.getElementById('value-input1'),
      document.getElementById('value-input2'),
      document.getElementById('value-input3'),
      document.getElementById('slider1'),
      document.getElementById('slider2'),
      document.getElementById('slider3'),
    ];
    // Using ... is a modern way, and older browsers do not support it, so it's safer to iterate manually
    for (const input of document.querySelectorAll('input[name^="properties["]')) {
      inputs.push(input);
    }

    for (const input of inputs) {
      input.addEventListener('change', function () {
        if (input.id == 'ledToggle') {
          if (input.checked) {
            document.getElementById('ledToggleRGB').checked = false;
            viewer.pxLEDToggle(true, 1);
            if (mobileViewer !== null) {
              mobileViewerviewer.pxLEDToggle(true, 1);
            }
          } else {
            viewer.pxLEDToggle(false);
            if (mobileViewer !== null) {
              mobileViewer.pxLEDToggle(false);
            }
          }
        }
        if (input.id == 'ledToggleRGB') {
          if (input.checked) {
            document.getElementById('ledToggle').checked = false;
            viewer.pxLEDToggle(true, 2);
            if (mobileViewer !== null) {
              mobileViewerviewer.pxLEDToggle(true, 2);
            }
          } else {
            viewer.pxLEDToggle(false);
            if (mobileViewer !== null) {
              mobileViewer.pxLEDToggle(false);
            }
          }
        }
        self.updatePrice();
        window.debounce(
          function () {
            self.updateLedPrice();
          }.bind(this),
          250
        )();
      });
    }

    document.getElementsByName(self.colorSelectorName).forEach((colorInput) => {
      colorInput.addEventListener('change', function () {
        let _this = this;
        let images = {
          lamp: document.querySelectorAll('.free-accessory-toggle'),
          screen: document.querySelectorAll('input[value="' + self.translations.screen_wall + '"]'),
          glass: document.querySelectorAll(
            'input[value="' + self.translations.frameless_glass + '"]'
          ),
          aluminium: document.querySelectorAll('input[value="' + self.translations.aluminium_wall + '"]'),
        };
        switch (_this.value) {
          case self.translations.color_white:
            images.lamp.forEach((input) => {
              let img = input.closest('.ac-size').querySelector('img');
              img.src = img.dataset.white;
            });
            images.screen.forEach((input) => {
              let img = input.closest('.box-size').querySelector('img');
              img.src = img.dataset.white;
            });
            images.glass.forEach((input) => {
              let img = input.closest('.box-size').querySelector('img');
              img.src = img.dataset.white;
            });
            images.aluminium.forEach((input) => {
              let img = input.closest('.box-size').querySelector('img');
              img.src = img.dataset.white;
            });
            break;
          default:
            images.lamp.forEach((input) => {
              let img = input.closest('.ac-size').querySelector('img');
              img.src = img.dataset.black;
            });
            images.screen.forEach((input) => {
              let img = input.closest('.box-size').querySelector('img');
              img.src = img.dataset.black;
            });
            images.glass.forEach((input) => {
              let img = input.closest('.box-size').querySelector('img');
              img.src = img.dataset.black;
            });
            images.aluminium.forEach((input) => {
              let img = input.closest('.box-size').querySelector('img');
              img.src = img.dataset.black;
            });
            break;
        }
       /* if (!self.mainProduct) {
          self.updateSelectedColor(colorInput.value);
        }*/
      });
    });
  };

  /**
   * Find the closest variant based on price
   *
   * @param {number} targetPrice
   * @returns {*}
   */
  findClosestVariant = (targetPrice) => {
    let self = this;
    let closestVariant;
    let minDifference = Infinity;

    for (const variant of self.variants) {
      if (self.findClosest(targetPrice, variant.price, minDifference)) {
        minDifference = Math.abs(targetPrice - variant.price / 100);
        closestVariant = variant;
      }
    }

    /*for (const option of self.options_with_values) {
      for (const value of option.values) {
        const variant = self.variants.find((v) => v.option1 === value)

        if (variant) {
          if (self.findClosest(targetPrice, variant.price, minDifference)) {
            minDifference = Math.abs(targetPrice - (variant.price / 100))
            closestVariant = variant
          }
        }
      }
    }*/

    return closestVariant;
  };

  findClosestVariantExternal = (targetPrice) => {
    let self = this;
    let closestVariant;
    let minDifference = Infinity;

    for (const variant of self.custom_product.variants) {
      if (self.findClosest(targetPrice, variant.price, minDifference)) {
        minDifference = Math.abs(targetPrice - variant.price / 100);
        closestVariant = variant;
      }
    }

    return closestVariant;
  };

  /**
   * Find the closest variant price based on the target price
   *
   * @param {number} targetPrice
   * @param {number} variantPrice
   * @param {number} minDifference
   * @returns {boolean}
   */
  findClosest = (targetPrice, variantPrice, minDifference) => {
    const difference = Math.abs(targetPrice - variantPrice / 100);

    return difference < minDifference;
  };

  /**
   * Find the closest variant based on price and type of wall
   *
   * @param {number} targetPrice
   * @param {string} type
   * @returns {{product: number, variant: number, price: number}}
   */
  findClosestVariantByType = (targetPrice, type) => {
    let self = this;
    let product = self[type + '_walls'];

    let minDifference = Infinity;
    let closestVariant = null;
    for (const i in product.variants) {
      const variant = product.variants[i];
      if (self.findClosest(targetPrice, variant.price, minDifference)) {
        minDifference = Math.abs(targetPrice - variant.price / 100);
        closestVariant = variant;
      }
    }

    if (closestVariant !== null) {
      return {
        product: product.id,
        variant: closestVariant.id,
        price: closestVariant.price / 100,
      };
    } else {
      return {
        product: product.id,
        variant: null,
        price: 0.0,
      };
    }
  };

  /**
   * Used to find the closest variant for the LED stripes
   *
   * @param {number} width
   * @param {number} length
   */
  findClosestVariantByWidthAndLength = (width, length) => {
    let self = this;

    width = Math.ceil(width);
    length = Math.ceil(length);

    let selectedVariant = null;

    return self.findByWidthAndLength(width, length);

    /*for(let i = width i < 10 i++) {
            for (let j = length j < 10 j++) {
                selectedVariant = self.findByWidthAndLength(i,j)
                if (selectedVariant) {
                    return selectedVariant
                }
            }
        }*/
  };

  /**
   * Find the LED stripe variant based on width and length
   *
   * @param {number} width
   * @param {number} length
   * @returns {null|any}
   */
  findByWidthAndLength = (width, length) => {
    let self = this;
    const isRGBChecked = document.getElementById('ledToggleRGB').checked;
    const isLEDChecked = document.getElementById('ledToggle').checked;

    if (isRGBChecked || isLEDChecked) {
      return self.led_stripes.variants.find(variant => 
        isRGBChecked ? variant.options[0] === 'RGB' : variant.options[0] !== 'RGB'
      );
    }

    /*for (const variant of self.led_stripes.variants) {
      if (document.getElementById('ledToggleRGB').checked) {
        if (variant.options[0] === 'RGB') {
          return variant;
        }
      }
      if (document.getElementById('ledToggle').checked) {
        if (variant.options[0] !== 'RGB') {
          return variant;
        }
      }
    }*/

    return null;
  };

  /**
   * Find the closest selected main variant by the checked input
   *
   * @param {HTMLElement} input
   *
   * @returns {{product: string, variant: string}}
   */
  findClosestVariantByInput = (input) => {
    const priceElement = input.closest('label').querySelector('.price');
    return {
      variant: priceElement.dataset.variant_id,
      product: priceElement.dataset.product_id,
    };
  };

  /**
   * Find the correct variant for a free accessory by the selected color
   *
   * @param {Object} accessory
   * @returns {any}
   */
  findFreeAccessoryVariant = (accessory) => {
    let self = this;
    let freeAccessory = accessory || self.free_accessories[0];
    if (!freeAccessory) {
      return null;
    }
    let color = document.querySelector(
      'input[name="' + self.colorSelectorName + '"]:checked'
    ).value;
    color = color.trim();

    if (color != self.colors.white) {
      if (color != self.colors.anthracite) {
        color = self.colors.anthracite;
      }
    }
    let selectedVariant = null;
    let fallbackVariant = null;
    
    for (let variant of freeAccessory.variants) {
      // Store the first available variant as fallback
      if (!fallbackVariant && variant.available) {
        fallbackVariant = variant;
      }
      
      // Check if the color matches
      const colorMatches = variant.option1 && variant.option1.indexOf(color) > -1;

      if (!colorMatches) {
        continue;
      }

      // Wattage is the deciding second option for heaters only
      if (variant.option2 === self.lampWatt) {
        return variant;
      }

      // Otherwise keep the first available color match, whatever the second option is
      if (!selectedVariant && variant.available) {
        selectedVariant = variant;
      }
    }

    // If no exact match found, return fallback or first variant
    return selectedVariant || fallbackVariant || freeAccessory.variants[0] || null;
  };

  /**
   * Add to cart functionality on the PERGOLUX Maßanfertigung page
   */
  addToCartConfig = () => {
    let self = this;
    let addToCartVariants = [];
    let toRemove = [];
    let formData = {};
    let addLedColorToPergola = false
    formData['properties'] = {};
    formData['id'] = self.originalPriceVariant.id;
    formData['quantity'] = 1;
    formData['properties'][self.translations.configurator_height] = self.formatSizeValue(self.sizes.height, 'configurator');
    formData['properties'][self.translations.configurator_width] = self.formatSizeValue(self.sizes.width, 'configurator');
    formData['properties'][self.translations.configurator_length] = self.formatSizeValue(self.sizes.length, 'configurator');
    formData['properties'][self.translations.structure_width] =
      self.formatSizeValue(self.sizes.structure_width, 'structure');
    formData['properties'][self.translations.structure_length] =
      self.formatSizeValue(self.sizes.structure_length, 'structure');
    formData['properties'][self.translations.structure_height] =
      self.formatSizeValue(self.sizes.structure_height, 'structure');
    formData['properties'][self.translations.walkthrough_height] =
      self.formatSizeValue(self.sizes.walkthrough_height, 'structure');

    for (let input of document.querySelectorAll('input[name^="properties["]')) {
      if (input.checked && input.value !== self.disabledWallValue) {
        if (self.freeAccessoryProperties.includes(input.name)) {
          let freeAccessoryVariant = self.findFreeAccessoryVariant(
            self.free_accessories[self.freeAccessoryProperties.indexOf(input.name)]
          );
          let obj = {};
          obj['id'] = freeAccessoryVariant.id;
          obj['quantity'] = 1;
          obj['properties'] = {};
          obj['properties'][self.translations.market] = self.translations.market_text;
          obj['properties']['_unique_id'] = this.generateRandomId();
          addToCartVariants.push(obj);
          obj = null;
        } else if (
          input.name === self.ledStripePropery ||
          input.name === self.ledStripeProperyRGB
        ) {
          if (self.ledStripeVariant) {
            addToCartVariants.push(self.buildLedProperties(self.ledStripeVariant));
            addLedColorToPergola = true
          }
        } else if (self.wallProperties.includes(input.name)) {
          let externalProduct = self.findClosestVariantByInput(input);
          let type = 'screen';
          if (externalProduct.product == self.glass_walls.id) {
            type = 'glass';
          } else if (externalProduct.product == self.wood_walls.id) {
              type = 'wood';
          } else if (externalProduct.product == self.aluminium_walls.id) {
              type = 'aluminium';
          }
          if (self.walls.width.includes(input.name)) {
            addToCartVariants.push(
              self.buildScreenProperties(externalProduct.variant, 'width', type, input.name)
            );
            /*addToCartVariants.push({'id': externalProduct.variant, 'quantity': 1, 'properties': {'Attribute': 'Width screen', 'Width': self.sizes.screen_width.width, 'Height': self.sizes.screen_width.height}})*/
          }
          if (self.walls.length.includes(input.name)) {
            let type = 'screen';
            if (externalProduct.product == self.glass_walls.id) {
              type = 'glass';
            } else if (externalProduct.product == self.wood_walls.id) {
                type = 'wood';
            } else if (externalProduct.product == self.aluminium_walls.id) {
                type = 'aluminium';
            }
            addToCartVariants.push(
              self.buildScreenProperties(externalProduct.variant, 'length', type, input.name)
            );
            /*addToCartVariants.push({'id': externalProduct.variant, 'quantity': 1, 'properties': {'Attribute': 'Length screen', 'Width': self.sizes.screen_length.width, 'Height': self.sizes.screen_length.height}})*/
          }
        } else if (self.colorSelectorName === input.name) {
          formData['properties'][self.translations.color] = input.value;
        } else if ('properties[' + self.translations.mounting + ']' === input.name) {
          formData['properties'][self.translations.mounting] = input.value;
        }
      }
    }

    formData['properties']['_unique_id'] = this.generateRandomId();

    if(addLedColorToPergola) {
      formData["properties"][self.translations.led_color] = self.ledStripeVariant.options[0]
    } else {
      formData["properties"][self.translations.led_color] = self.translations.no_led_color
    }

    // Mounting Offer
    const mountElement = document.querySelector('#mounting_option');
    if (mountElement) {
      let obj = {};
      obj['id'] = mountElement.value;
      obj['quantity'] = 1;
      addToCartVariants.push(obj);
      obj = null;
    }

    const button = document.querySelector('button[name="add"]:focus'); // :focus to support multiple buttons in the same form (e.g., sticky button)
    button?.classList.add('loading');

    resilientFetch('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify(formData),
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(async (response) => {
      // Check the response or perform additional actions if needed
      if (addToCartVariants.length) {
        self.addToCartVariant(addToCartVariants);
      } else {
        const url = window.routes.cart_add_url;
        await this.getCart();
        window.theme.modals.cart.open();
        //window.location.href = "/cart"
      }
    }).catch(() => {
      document.dispatchEvent(new CustomEvent('cart:error'));
      document.getElementById('cart-error-banner')?.classList.remove('hidden');
    }).finally(() => {
      button?.classList.remove('loading');
    });
  };

  /**
   * Add to cart functionality for custom sized pergolas
   */
  addToCartCustom = () => {
    let self = this;
    let addToCartVariants = [];
    let toRemove = [];
    let formData = {};

    formData['id'] = self.originalPriceVariant.id;
    formData['properties'] = {};
    if (document.querySelector('input[data-index="option1"]:checked') !== null) {
      formData['properties'][document.querySelector('input[data-index="option1"]:checked').name] =
        document.querySelector('input[data-index="option1"]:checked').value.toString();
    }
    if (document.querySelector('input[data-index="option2"]:checked') !== null) {
      formData['properties'][document.querySelector('input[data-index="option2"]:checked').name] =
        document.querySelector('input[data-index="option2"]:checked').value.toString();
    }
    if (document.querySelector('input[data-index="option3"]:checked') !== null) {
      formData['properties'][document.querySelector('input[data-index="option3"]:checked').name] =
        document.querySelector('input[data-index="option3"]:checked').value.toString();
    }
    if (document.querySelector('input[name="quantity"]') !== null) {
      formData['quantity'] = document.querySelector('input[name="quantity"]').value;
    } else {
      formData['quantity'] = 1;
    }
    formData['properties'][self.translations.configurator_height] = self.formatSizeValue(self.sizes.height, 'configurator');
    formData['properties'][self.translations.configurator_width] = self.formatSizeValue(self.sizes.width, 'configurator');
    formData['properties'][self.translations.configurator_length] = self.formatSizeValue(self.sizes.length, 'configurator');
    formData['properties'][self.translations.structure_width] =
      self.formatSizeValue(self.sizes.structure_width, 'structure');
    formData['properties'][self.translations.structure_length] =
      self.formatSizeValue(self.sizes.structure_length, 'structure');
    formData['properties'][self.translations.structure_height] =
      self.formatSizeValue(self.sizes.structure_height, 'structure');
    formData['properties'][self.translations.walkthrough_height] =
      self.formatSizeValue(self.sizes.walkthrough_height, 'structure');

    for (let input of document.querySelectorAll('input[name^="properties["]')) {
      if (input.checked && input.value !== self.disabledWallValue) {
        if (self.freeAccessoryProperties.includes(input.name)) {
          let freeAccessoryVariant = self.findFreeAccessoryVariant(
            self.free_accessories[self.freeAccessoryProperties.indexOf(input.name)]
          );
          let obj = {};
          obj['id'] = freeAccessoryVariant.id;
          obj['quantity'] = 1;
          obj['properties'] = {};
          obj['properties'][self.translations.market] = self.translations.market_text;
          addToCartVariants.push(obj);
          obj = null;
        } else if (
          input.name === self.ledStripePropery ||
          input.name === self.ledStripeProperyRGB
        ) {
          if (self.ledStripeVariant) {
            addToCartVariants.push(self.buildLedProperties(self.ledStripeVariant));
          }
        } else {
          let externalProduct = self.findClosestVariantByInput(input);
          if (self.walls.width.includes(input.name)) {
            addToCartVariants.push(self.buildScreenProperties(externalProduct.variant, 'width'));
            /*addToCartVariants.push({'id': externalProduct.variant, 'quantity': 1, 'properties': {'Attribute': 'Width screen', 'Width': self.sizes.screen_width.width, 'Height': self.sizes.screen_width.height}})*/
          }
          if (self.walls.length.includes(input.name)) {
            addToCartVariants.push(self.buildScreenProperties(externalProduct.variant, 'length'));
            /*addToCartVariants.push({'id': externalProduct.variant, 'quantity': 1, 'properties': {'Attribute': 'Length screen', 'Width': self.sizes.screen_length.width, 'Height': self.sizes.screen_length.height}})*/
          }
        }
      }
    }

    formData['properties']['_unique_id'] = this.generateRandomId();

    const button = document.querySelector('button[name="add"]:focus'); // :focus to support multiple buttons in the same form (e.g., sticky button)
    button?.classList.add('loading');

    resilientFetch('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify(formData),
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(async (response) => {
      // Check the response or perform additional actions if needed
      if (addToCartVariants.length) {
        self.addToCartVariant(addToCartVariants);
      } else {
        const url = window.routes.cart_add_url;
        await this.getCart();

        window.theme.modals.cart.open();
        //window.location.href = "/cart"
      }
    }).catch(() => {
      document.dispatchEvent(new CustomEvent('cart:error'));
      document.getElementById('cart-error-banner')?.classList.remove('hidden');
    }).finally(() => {
      button?.classList.remove('loading');
    });
  };

  /**
   * Handling the add to cart functionality
   */
  addToCart = () => {
    let self = this;
    let addToCartVariants = [];
    let toRemove = [];
    let formData = null;

    self.setStructureVariant();
    self.setSizes(
      document.getElementById('value-input1').value.replace('mm'),
      document.getElementById('value-input2').value.replace('mm'),
      document.getElementById('value-input3').value.replace('mm')
    );

    if (!self.mainProduct) {
      self.addToCartConfig();
    } else {
      if (document.querySelector('.custom-size').classList.contains('active')) {
        self.addToCartCustom();
      } else {
        let size = document
          .querySelector('input[data-index="option2"]:checked')
          .value.replace('m', '')
          .split('x');
        self.setSizes(parseFloat(size[0]) * 1000, parseFloat(size[1]) * 1000, 2000);

        formData = new FormData(document.querySelector('[id^="AddToCartForm"]'));
        for (let input of document.querySelectorAll('input[name^="properties["]')) {
          if (input.checked && input.value !== self.disabledWallValue) {
            if (self.freeAccessoryProperties.includes(input.name)) {
              let freeAccessoryVariant = self.findFreeAccessoryVariant(
                self.free_accessories[self.freeAccessoryProperties.indexOf(input.name)]
              );
              let obj = {};
              obj['id'] = freeAccessoryVariant.id;
              obj['quantity'] = 1;
              obj['properties'] = {};
              obj['properties'][self.translations.market] = self.translations.market_text;
              addToCartVariants.push(obj);
              obj = null;
            } else if (
              input.name === self.ledStripePropery ||
              input.name === self.ledStripeProperyRGB
            ) {
              if (self.ledStripeVariant) {
                addToCartVariants.push(self.buildLedProperties(self.ledStripeVariant));
              }
            } else {
              let externalProduct = self.findClosestVariantByInput(input);
              if (self.walls.width.includes(input.name)) {
                addToCartVariants.push(
                  self.buildScreenProperties(externalProduct.variant, 'width')
                );
              }
              if (self.walls.length.includes(input.name)) {
                addToCartVariants.push(
                  self.buildScreenProperties(externalProduct.variant, 'length')
                );
              }
            }
          }
        }

        const button = document.querySelector('button[name="add"]:focus'); // :focus to support multiple buttons in the same form (e.g., sticky button)
        button?.classList.add('loading');

        resilientFetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          /*headers: {
                        'ContentType': 'application/json'
                    }*/
        }).then(async (response) => {
          // Check the response or perform additional actions if needed
          if (addToCartVariants.length) {
            self.addToCartVariant(addToCartVariants);
          } else {
            const url = window.routes.cart_add_url;
            await this.getCart();

            window.theme.modals.cart.open();
            //window.location.href = "/cart"
          }
        }).catch(() => {
          document.dispatchEvent(new CustomEvent('cart:error'));
          document.getElementById('cart-error-banner')?.classList.remove('hidden');
        }).finally(() => {
          button?.classList.remove('loading');
        });
      }
    }
  };

  /**
   * Add to cart the selected variants from the external products
   *
   * @param {array} variants Array of variants to add to cart
   * @returns {Promise<unknown>}
   */
  addToCartVariant = (variants) => {
    let formData = {
      items: variants,
    };

    const button = document.querySelector('button[name="add"]:focus'); // :focus to support multiple buttons in the same form (e.g., sticky button)
    button?.classList.add('loading');

    resilientFetch('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify(formData),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error();
        // window.theme.modals.cart.open();
        //window.theme.cart.addToCart(event)
        const url = window.routes.cart_add_url;
        await this.getCart();

        window.theme.modals.cart.open();
        //window.location.href = "/cart"
      })
      .catch((error) => {
        console.log(error);
        document.dispatchEvent(new CustomEvent('cart:error'));
        document.getElementById('cart-error-banner')?.classList.remove('hidden');
      })
      .finally(() => {
        button?.classList.remove('loading');
      });
  };

  buildLedProperties = (variant) => {
    let self = this;
    let obj = {};
    obj['id'] = parseInt(variant.id);
    obj['quantity'] = 1;
    obj['properties'] = {};
    obj['properties'][self.translations.width] = self.formatSizeValue(self.sizes.led.width, 'structure');
    obj['properties'][self.translations.length] = self.formatSizeValue(self.sizes.led.length, 'structure');
    obj['properties'][self.translations.market] = self.translations.market_text;
    obj['properties']['_unique_id'] = this.generateRandomId();
    //obj["properties"][self.translations.color] = self.translations.warm_white
    obj["properties"][self.translations.color] = variant.options[0];

    return obj;
  };
  
  buildScreenProperties = (variant, type, productType, input) => {
    let self = this;
    let obj = {};
    obj['id'] = parseInt(variant);
    obj['quantity'] = 1;
    obj['properties'] = {};
    obj['properties']['_unique_id'] = this.generateRandomId();

    switch (type) {
      case 'width':
        obj['properties'][self.translations.width] = self.formatSizeValue(self.sizes.screen_width.width, 'structure');
        obj['properties'][self.translations.height] = self.formatSizeValue(self.sizes.screen_width.height, 'structure');
        switch (productType) {
          case 'glass':
            if (input == 'properties[' + self.translations.left_side + ']') {
              obj['properties'][self.translations.screen_side.replace(' -', '')] =
                self.translations.left_glass;
            } else if (input == 'properties[' + self.translations.right_side + ']') {
              obj['properties'][self.translations.screen_side.replace(' -', '')] =
                self.translations.right_glass;
            }
            break;
          case 'aluminium':
            if (input == 'properties[' + self.translations.left_side + ']') {
              obj['properties'][self.translations.screen_side.replace(' -', '')] = self.translations.left_aluminium;
            } else if (input == 'properties[' + self.translations.right_side + ']') {
              obj['properties'][self.translations.screen_side.replace(' -', '')] = self.translations.right_aluminium;
            }
            break;
          default:
            if (input == 'properties[' + self.translations.left_side + ']') {
              obj['properties'][self.translations.screen_side.replace(' -', '')] =
                self.translations.left_screen;
            } else if (input == 'properties[' + self.translations.right_side + ']') {
              obj['properties'][self.translations.screen_side.replace(' -', '')] =
                self.translations.right_screen;
            }
            break;
        }
        break;
      case 'length':
        let width = self.sizes.screen_length.width;
        let height = self.sizes.screen_length.height;

        let useXl = false;

        if (self.sizes.length > 5000) {
          useXl = true;
        }

        let sideTranslation = null;
        switch (productType) {
          case 'glass':
            if (useXl) {
              if (
                input ==
                'properties[' + self.translations.front_side + ' ' + self.translations.left + ']'
              ) {
                sideTranslation = self.translations.front_left_glass;
              } else if (
                input ==
                'properties[' + self.translations.front_side + ' ' + self.translations.right + ']'
              ) {
                sideTranslation = self.translations.front_right_glass;
              } else if (
                input ==
                'properties[' + self.translations.back_side + ' ' + self.translations.left + ']'
              ) {
                sideTranslation = self.translations.back_left_glass;
              } else if (
                input ==
                'properties[' + self.translations.back_side + ' ' + self.translations.right + ']'
              ) {
                sideTranslation = self.translations.back_right_glass;
              }
            } else {
              if (
                input ==
                'properties[' + self.translations.front_side + ' ' + self.translations.left + ']'
              ) {
                sideTranslation = self.translations.front_glass;
              } else if (
                input ==
                'properties[' + self.translations.back_side + ' ' + self.translations.left + ']'
              ) {
                sideTranslation = self.translations.back_glass;
              }
            }
            (obj.properties[self.translations.width] = self.formatSizeValue(self.sizes.screen_length.width, 'structure')),
              (obj.properties[self.translations.height] =
                self.formatSizeValue(self.sizes.screen_length.height, 'structure')),
              (obj.properties[self.translations.screen_side.replace(' -', '')] = sideTranslation);
            break;
          case 'aluminium':
            if (useXl) {
              if (
                input ==
                'properties[' + self.translations.front_side + ' ' + self.translations.left + ']'
              ) {
                sideTranslation = self.translations.front_left_aluminium;
              } else if (
                input ==
                'properties[' + self.translations.front_side + ' ' + self.translations.right + ']'
              ) {
                sideTranslation = self.translations.front_right_aluminium;
              } else if (
                input ==
                'properties[' + self.translations.back_side + ' ' + self.translations.left + ']'
              ) {
                sideTranslation = self.translations.back_left_aluminium;
              } else if (
                input ==
                'properties[' + self.translations.back_side + ' ' + self.translations.right + ']'
              ) {
                sideTranslation = self.translations.back_right_aluminium;
              }
            } else {
              if (
                input ==
                'properties[' + self.translations.front_side + ' ' + self.translations.left + ']'
              ) {
                sideTranslation = self.translations.front_aluminium;
              } else if (
                input ==
                'properties[' + self.translations.back_side + ' ' + self.translations.left + ']'
              ) {
                sideTranslation = self.translations.back_aluminium;
              }
            }
            (obj.properties[self.translations.width] = self.sizes.screen_length.width.toString()),
              (obj.properties[self.translations.height] =
                self.sizes.screen_length.height.toString()),
              (obj.properties[self.translations.screen_side.replace(' -', '')] = sideTranslation);
            break;
          default:
            if (useXl) {
              if (
                input ==
                'properties[' + self.translations.front_side + ' ' + self.translations.left + ']'
              ) {
                sideTranslation = self.translations.front_left_screen;
              } else if (
                input ==
                'properties[' + self.translations.front_side + ' ' + self.translations.right + ']'
              ) {
                sideTranslation = self.translations.front_right_screen;
              } else if (
                input ==
                'properties[' + self.translations.back_side + ' ' + self.translations.left + ']'
              ) {
                sideTranslation = self.translations.back_left_screen;
              } else if (
                input ==
                'properties[' + self.translations.back_side + ' ' + self.translations.right + ']'
              ) {
                sideTranslation = self.translations.back_right_screen;
              }
            } else {
              if (
                input ==
                'properties[' + self.translations.front_side + ' ' + self.translations.left + ']'
              ) {
                sideTranslation = self.translations.front_screen;
              } else if (
                input ==
                'properties[' + self.translations.back_side + ' ' + self.translations.left + ']'
              ) {
                sideTranslation = self.translations.back_screen;
              }
            }
            (obj.properties[self.translations.width] = self.formatSizeValue(self.sizes.screen_length.width, 'structure')),
              (obj.properties[self.translations.height] =
                self.formatSizeValue(self.sizes.screen_length.height, 'structure')),
              (obj.properties[self.translations.screen_side.replace(' -', '')] = sideTranslation);
            break;
        }
        break;
    }
    if (self.mainProduct) {
      if (document.querySelector('input[data-index="option1"]:checked') !== null) {
        let color = document.querySelector('input[data-index="option1"]:checked').value.split(' ');
        if (color[0] === 'Schwarz') {
          color[0] = 'Anthrazit';
        }
        obj['properties'][self.translations.color] = color[0];
      }
    } else {
      let color = document
        .querySelector('input[name="' + pergolaProduct.colorSelectorName + '"]:checked')
        .value.split(' ');
      if (color[0] === 'Schwarz') {
        color[0] = 'Anthrazit';
      }
      obj['properties'][self.translations.color] = color[0];
    }

    return obj;
  };

  /**
   * Calculate square meters
   *
   * @param {number} value1
   * @param {number} value2
   * @returns {number}
   */
  calculateSquareMeters = (value1, value2) => {
    return value1 * value2;
  };

  /**
   * Get the prices from all the checked inputs
   *
   * @returns {number} Total price
   */
  getInputPrices = () => {
    let self = this;
    let basePrice = 0;
    const optionInputs = document.querySelectorAll('input[name^="properties["]');
    optionInputs.forEach((input) => {
      if (input.checked) {
        let currentInput = input.closest('label').querySelector('.price');
        let priceText = currentInput.innerText.replace(/\D/g, '');
        //Check if we are in the UK shop
        if (Shopify.currency.active == 'GBP') {
          priceText = priceText.replace(',', '.').replace('£', '');
        } else if (Shopify.currency.active == 'CHF') {
          priceText = priceText.replace(',', '.').replace('CHF', '').replace('’', '');
        }
        let price = parseFloat(priceText) ?? 0;
        if (!isNaN(price) && price > 0) {
          basePrice += price;
        }
      }
    });

    return basePrice;
  };

  /**
   * Update the total price input
   *
   * @param {number} basePrice
   */
  updateTotalPrice = (basePrice) => {
    let self = this;

    // Iterate through checked options and update basePrice
    const pergolaPrice = basePrice;
    const accessoriesPrice = self.getInputPrices();
    basePrice += accessoriesPrice;
    // Update the displayed total price
    const totalPriceElement = document.getElementById('total-price');
    totalPriceElement.value = new Intl.NumberFormat(Shopify.locale + '-' + Shopify.country, {
      style: 'currency',
      currency: Shopify.currency.active,
      currencyDisplay: currencyDisplay,
      maximumFractionDigits: currencyDigits,
    }).format(basePrice);

    document.dispatchEvent(
      new CustomEvent('custom-design:update', {
        detail: {
          totalPrice: basePrice,
          pergolaPrice: pergolaPrice,
          accessoriesPrice: accessoriesPrice,
          squareMeters: self.calculateSquareMeters(self.lengthInMeters, self.widthInMeters) || 0,
        },
      })
    );
    /*
        // Send the updated total price to the server
        $.ajax({
            type: 'POST',
            url: theme.routes.updateCart,
            data: { total_price: basePrice },
            dataType: 'json',
        })
        */
  };

  calculateSizes = () => {
    let self = this;
    let width = self.sizes.width;
    let length = self.sizes.length;
    let height = self.sizes.height;
    let widthFeet = self.getColumnFeet(self.sizes.width, self.structure_variant, false);
    let lengthFeet = self.getColumnFeet(self.sizes.length, false, false);

    let structure_width = width - 40 * widthFeet;
    let structure_length = length - 40 * 2;
    let heightOffset = self.VERSION_HEIGHT_OFFSETS[self.pergolaVersion] || self.VERSION_HEIGHT_OFFSETS[self.DEFAULT_VERSION];
    let structure_height = height - heightOffset;
    let walkthrough_height = structure_height - 175;

    self.sizes = {
      width: width,
      length: length,
      height: height,
      structure_width: structure_width,
      structure_length: structure_length,
      structure_height: structure_height,
      walkthrough_height: walkthrough_height,
      columns_length: lengthFeet,
      columns_length_feet: lengthFeet,
      columns_width: widthFeet,
      columns_width_feet: widthFeet,
      screen_width: {
        width: structure_width - 120 * widthFeet - 1,
        height: structure_height - 177,
      },
      screen_length: {
        width: (structure_length - 120 * lengthFeet) / (lengthFeet - 1) - 1,
        height: structure_height - 177,
      },
      led: {
        width: structure_width - 265,
        length: structure_length - 265,
      },
    };
  };

  setSizes = (width, length, height) => {
    let self = this;
    self.sizes.height = parseFloat(height);
    self.sizes.length = parseFloat(length);
    self.sizes.width = parseFloat(width);

    self.calculateSizes();
  };

  /**
   * Calculate the price based on the formula and the square meter value
   *
   * @param {number} basePrice
   * @param {number} squareMeterValue
   * @param {number} minSqm
   * @param {number} pricePerSqm
   * @param {null|string} type
   * @param value
   * @returns {number}
   */
  calculatePrice = (
    basePrice,
    squareMeterValue,
    minSqm,
    pricePerSqm,
    type = null,
    value = null
  ) => {
    let self = this;
    let min_value = 0;
    switch (type) {
      case 'width':
        if (!value || value === null) {
          value = self.sizes.width / 1000;
        }

        min_value = parseFloat(document.getElementById('slider1').attributes.min.value) / 1000;
        return basePrice + value * pricePerSqm;
      case 'length':
        if (!value || value === null) {
          value = self.sizes.length / 1000;
        }
        min_value = parseFloat(document.getElementById('slider2').attributes.min.value) / 1000;

        return basePrice + value * pricePerSqm;
      default:
        return basePrice + squareMeterValue * pricePerSqm;
    }
  };

  /**
   * Update pricing data on the frontend
   */
  updatePrice = () => {
    let self = this;

    self.heightInMeters = parseFloat(document.getElementById('slider3').value) / 1000;
    self.lengthInMeters = parseFloat(document.getElementById('slider2').value) / 1000;
    self.widthInMeters = parseFloat(document.getElementById('slider1').value) / 1000;

    self.setSizes(
      document.getElementById('value-input1').value.replace('mm'),
      document.getElementById('value-input2').value.replace('mm'),
      document.getElementById('value-input3').value.replace('mm')
    );

    let calculatedtPrice = null;

    if (Shopify.currency.active == 'GBP') {
      calculatedtPrice = parseFloat(
        document
          .getElementById('total-price')
          .value.replace('.', '')
          .replace(',', '.')
          .replace(' ', '')
          .replace('£', '')
      );
    } else if (Shopify.currency.active == 'CHF') {
      calculatedtPrice = parseFloat(
        document
          .getElementById('total-price')
          .value.replace('.', '')
          .replace(',', '.')
          .replace(' ', '')
          .replace('CHF', '')
          .replace('’', '')
      );
    } else {
      calculatedtPrice = parseFloat(document.getElementById('total-price').value.replace(' ', ''));
    }

    const calculatedSqmValues = {
      lengthXWidth: self.calculateSquareMeters(self.lengthInMeters, self.widthInMeters),
      lengthXHeight: self.calculateSquareMeters(self.lengthInMeters, self.heightInMeters),
      widthXHeight: self.calculateSquareMeters(self.widthInMeters, self.heightInMeters),
    };
    const calculatedPrice = self.calculatePrice(
      self.FORMULA.basePrice,
      calculatedSqmValues.lengthXWidth,
      self.FORMULA.minSqm,
      self.FORMULA.pricePerSqm
    );
    self.originalPriceVariant = calculatedPrice;

    let lengthXHeightXl = null;
    let accessoryCalculatedLengthWidth = self.sizes.screen_length.width / 1000;
    let accessoryCalculatedLengthHeight = self.sizes.screen_length.height / 1000;
    let accessoryCalculatedLengthSqmMeters = self.calculateSquareMeters(
      accessoryCalculatedLengthWidth,
      accessoryCalculatedLengthHeight
    );
    let accessoryCalculatedWidthWidth = self.sizes.screen_width.width / 1000;
    let accessoryCalculatedWidthHeight = self.sizes.screen_width.height / 1000;
    let accessoryCalculatedWidthSqmMeters = self.calculateSquareMeters(
      accessoryCalculatedWidthWidth,
      accessoryCalculatedWidthHeight
    );

    const calculatedPricesBase = {
      wood: self.calculatePrice(
        self.FORMULA.woodBasePrice,
        accessoryCalculatedWidthSqmMeters,
        self.FORMULA.woodMinSqm,
        self.FORMULA.woodPricePerSqm,
        'width',
        accessoryCalculatedWidthSqmMeters
      ),
      wood_length: self.calculatePrice(
        self.FORMULA.woodBasePrice,
        accessoryCalculatedLengthSqmMeters,
        self.FORMULA.woodMinSqm,
        self.FORMULA.woodPricePerSqm,
        'length',
        accessoryCalculatedLengthSqmMeters
      ),
      screen: self.calculatePrice(
        self.FORMULA.screenBasePrice,
        accessoryCalculatedWidthSqmMeters,
        self.FORMULA.screenMinSqm,
        self.FORMULA.screenPricePerSqm,
        'width',
        accessoryCalculatedWidthSqmMeters
      ),
      screen_length: self.calculatePrice(
        self.FORMULA.screenBasePrice,
        accessoryCalculatedLengthSqmMeters,
        self.FORMULA.screenMinSqm,
        self.FORMULA.screenPricePerSqm,
        'length',
        accessoryCalculatedLengthSqmMeters
      ),
      glass: self.calculatePrice(
        self.FORMULA.glassBasePrice,
        accessoryCalculatedWidthSqmMeters,
        self.FORMULA.glassMinSqm,
        self.FORMULA.glassPricePerSqm,
        'width',
        accessoryCalculatedWidthSqmMeters
      ),
      glass_length: self.calculatePrice(
        self.FORMULA.glassBasePrice,
        accessoryCalculatedLengthSqmMeters,
        self.FORMULA.glassMinSqm,
        self.FORMULA.glassPricePerSqm,
        'length',
        accessoryCalculatedLengthSqmMeters
      ),
      aluminium: self.calculatePrice(
        self.FORMULA.aluminiumBasePrice,
        accessoryCalculatedWidthSqmMeters,
        self.FORMULA.aluminiumMinSqm,
        self.FORMULA.aluminiumPricePerSqm,
        'width',
          accessoryCalculatedWidthSqmMeters
      ),
      aluminium_length: self.calculatePrice(
        self.FORMULA.aluminiumBasePrice,
        accessoryCalculatedLengthSqmMeters,
        self.FORMULA.aluminiumMinSqm,
        self.FORMULA.aluminiumPricePerSqm,
        'length',
          accessoryCalculatedLengthSqmMeters
      ),
    };

    self.calculatedPrices = {
      wood: self.findClosestWallPrice(calculatedPricesBase.wood, 'wood'),
      wood_length: self.findClosestWallPrice(calculatedPricesBase.wood_length, 'wood'),
      screen: self.findClosestWallPrice(calculatedPricesBase.screen, 'screen'),
      screen_length: self.findClosestWallPrice(calculatedPricesBase.screen_length, 'screen'),
      glass: self.findClosestWallPrice(calculatedPricesBase.glass, 'glass'),
      glass_length: self.findClosestWallPrice(calculatedPricesBase.glass_length, 'glass'),
      aluminium: self.findClosestWallPrice(calculatedPricesBase.aluminium, 'aluminium'),
      aluminium_length: self.findClosestWallPrice(calculatedPricesBase.aluminium_length, 'aluminium'),
    };

    if (self.update_price) {
      self.displayCalculatedPrice(calculatedPrice, ['totalPrice'], '');
    }

    self.displayCalculatedPrice(
      self.calculatedPrices.wood,
      ['woodPrice1', 'woodPrice2', 'woodPrice1Xl', 'woodPrice2Xl'],
      self.findClosestVariantByType(self.calculatedPrices.wood, 'wood')
    );
    self.displayCalculatedPrice(
      self.calculatedPrices.wood_length,
      ['woodPrice3', 'woodPrice4', 'woodPrice3Xl', 'woodPrice4Xl'],
      self.findClosestVariantByType(self.calculatedPrices.wood_length, 'wood')
    );

    self.displayCalculatedPrice(
      self.calculatedPrices.screen,
      ['screenPrice1', 'screenPrice2'],
      self.findClosestVariantByType(self.calculatedPrices.screen, 'screen')
    );
    self.displayCalculatedPrice(
      self.calculatedPrices.screen_length,
      ['screenPrice3', 'screenPrice4', 'screenPrice3Xl', 'screenPrice4Xl'],
      self.findClosestVariantByType(self.calculatedPrices.screen_length, 'screen')
    );

    self.displayCalculatedPrice(
      self.calculatedPrices.glass,
      [
        'glassPrice1',
        'glassPrice2',
        'glassPrice5',
        'glassPrice6',
        'glassfixPrice1',
        'glassfixPrice2',
      ],
      self.findClosestVariantByType(self.calculatedPrices.glass, 'glass')
    );
    self.displayCalculatedPrice(
      self.calculatedPrices.glass_length,
      [
        'glassPrice3',
        'glassPrice4',
        'glassPrice7',
        'glassPrice8',
        'glassfixPrice3',
        'glassfixPrice4',
        'glassPrice3Xl',
        'glassPrice4Xl',
        'glassPrice7Xl',
        'glassPrice8Xl',
        'glassfixPrice3Xl',
        'glassfixPrice4Xl',
      ],
      self.findClosestVariantByType(self.calculatedPrices.glass_length, 'glass')
    );

    self.displayCalculatedPrice(
      self.calculatedPrices.aluminium,
      ['aluminiumPrice1', 'aluminiumPrice2', 'aluminiumPrice1Xl', 'aluminiumPrice2Xl'],
      self.findClosestVariantByType(self.calculatedPrices.aluminium, 'aluminium')
    );

    self.displayCalculatedPrice(
      self.calculatedPrices.aluminium_length,
      ['aluminiumPrice3', 'aluminiumPrice4', 'aluminiumPrice3Xl', 'aluminiumPrice4Xl'],
      self.findClosestVariantByType(self.calculatedPrices.aluminium_length, 'aluminium')
    );

    if (self.update_price) {
      let closestVariantRes = self.findClosestVariant(calculatedtPrice);
      let formattedPrice, formattedPriceE;
      self.originalPriceVariant = self.findClosestVariant(calculatedPrice);
      document.getElementById('originalPrice').innerText = (
        self.originalPriceVariant.price / 100
      ).toLocaleString(self.locale);

      if (isNaN(calculatedtPrice)) {
        self.updateTotalPrice(parseFloat((self.originalPriceVariant.price / 100).toFixed(0)));
        //Check if we are in the UK shop
        if (Shopify.currency.active == 'GBP') {
          calculatedtPrice = parseFloat(
            document
              .getElementById('total-price')
              .value.replace('.', '')
              .replace(',', '.')
              .replace(' ', '')
              .replace('£', '')
          );
        } else if (Shopify.currency.active == 'CHF') {
          calculatedtPrice = parseFloat(
            document
              .getElementById('total-price')
              .value.replace('.', '')
              .replace(',', '.')
              .replace(' ', '')
              .replace('CHF', '')
              .replace('’', '')
          );
        } else if (Shopify.currency.active == 'USD') {
          calculatedtPrice = parseFloat(
            document
              .getElementById('total-price')
              .value.replace('$', '')
              .replace(',', '') // remove thousand separators (1,234.56)
              .replace(' ', '')
          );
        } else {
          calculatedtPrice = parseFloat(
            document.getElementById('total-price').value.replace('€', '').replace(' ', '')
          );
        }

        closestVariantRes = self.findClosestVariant(calculatedtPrice);
      }
      self.current_variant = self.originalPriceVariant;
      let calculatedTotalPrice = self.originalPriceVariant.price / 100 + self.getInputPrices();

      if (closestVariantRes) {
        //Check if we are in the UK shop
        if (Shopify.currency.active == 'GBP') {
          formattedPrice = self.getFormattedPrice(calculatedTotalPrice);
          formattedPriceE = calculatedTotalPrice;
        } else if (Shopify.currency.active == 'CHF') {
          formattedPrice = self.getFormattedPrice(calculatedTotalPrice);
          formattedPriceE = calculatedTotalPrice;
        } else {
          formattedPrice = calculatedTotalPrice.toLocaleString(self.locale);
          formattedPriceE = calculatedTotalPrice;
        }
      }

      document.getElementById('selectedValue').innerText = formattedPrice;
      document.getElementById('selectedValueb').innerText = formattedPrice;

      document
        .querySelectorAll('.calculatedPrice')
        .forEach((priceTargets) => (priceTargets.innerText = formattedPrice));

      //16192 / ((1 - 0.3) * 1)

      if (self.FORMULA.discount_percent) {
        let discount = 1 - (self.FORMULA.discount_percent / 100) * 1;
        const disco = formattedPriceE / discount;

        document.getElementById('ccCc').innerText = self.getFormattedPrice(disco.toFixed(0));
        document.getElementById('ccCcc').innerText = self.getFormattedPrice(disco.toFixed(0));

        document.querySelectorAll('.myCheck').forEach((span) => {
          span.closest('.original-price').classList.remove('hidden');
          span.textContent = '';
          span.textContent = self.getPriceLabel(span, disco).replace(/\s\s+/g, ' ');
        });

        document.querySelectorAll('.myCheckc').forEach((span) => {
          span.closest('.original-price').classList.remove('hidden');
          span.textContent = '';
          span.textContent = self.getPriceLabel(span, disco).replace(/\s\s+/g, ' ');
        });
      } else {
        document.querySelectorAll('.original-price').forEach((div) => {
          div.style.display = 'none';
        });
        document.getElementById('ccCc').style.display = 'none';
        document.getElementById('ccCcc').style.display = 'none';
        document
          .querySelectorAll('#discount.save-amount, #discountC.save-amount')
          .forEach((saveAmount) => (saveAmount.style.display = 'none'));
      }
      // Check the radio button corresponding to the closest variant
      if (self.originalPriceVariant) {
        document.getElementsByName('id').forEach((button) => {
          if (parseInt(button.value) === self.originalPriceVariant.id) {
            button.checked = true;
          }
        });
      }

      self.updateTotalPrice(parseFloat((self.originalPriceVariant.price / 100).toFixed(0)));
    } else {
      self.originalPriceVariant = self.findClosestVariantExternal(calculatedPrice);
      if (document.querySelector('.custom-size').classList.contains('active')) {
        document.querySelector('#customizator-price').innerHTML = parseFloat(
          (self.originalPriceVariant.price / 100).toFixed(0)
        ).toLocaleString(self.locale);

        document.querySelector(
          '.pergola-sliding-config .prices, .pergola-sliding-config .prices #selectedValue'
        ).innerHTML = parseFloat((self.originalPriceVariant.price / 100).toFixed(0)).toLocaleString(
          self.locale
        );
      }
      //self.displayCalculatedPrice(parseFloat((self.originalPriceVariant.price / 100).toFixed(0)), ['customizator-price'], '')
    }
  };

  /**
   * Update the prices for the LED stripes
   */
  updateLedPrice = () => {
    let self = this;
    self.ledStripeVariant = self.findClosestVariantByWidthAndLength(
      self.widthInMeters,
      self.lengthInMeters
    );
  };

  /**
   * Update the price labels
   *
   * @param {HTMLElement} span
   * @param {number} disco
   * @returns {*|string|string}
   */
  getPriceLabel = (span, disco) => {
    let self = this;
    span.innerText = self.getFormattedPrice(disco.toFixed(0));

    // Get the current content of the span
    const content = span.innerText;

    return content.replace(/\s\s+/g, ' ');
  };

  getFormattedPrice = (price) => {
    let self = this;

    if (!price) {
      return;
    }

    let numberPrice = new Number(price).toFixed(2);
    let stringPrice = String(numberPrice);

    if (stringPrice.indexOf('.') != -1) {
      stringPrice = stringPrice.replace('.', '');
    } else if (stringPrice.indexOf(',') != -1) {
      stringPrice = stringPrice.replace(',', '');
    } else {
      stringPrice += '00';
    }

    if (!stringPrice) {
      return;
    }

    if (typeof self.calculatedFormattedPrices[stringPrice] !== 'undefined') {
      return self.calculatedFormattedPrices[stringPrice];
    } else {
      self.calculatedFormattedPrices[stringPrice] = new Intl.NumberFormat(
        Shopify.locale + '-' + Shopify.country,
        {
          style: 'currency',
          currency: Shopify.currency.active,
          currencyDisplay: currencyDisplay,
          maximumFractionDigits: currencyDigits,
        }
      ).format(price);

      return self.calculatedFormattedPrices[stringPrice];
    }
  };

  getRandomInt = () => {
    return Math.floor(Math.random() * (9999999999 - 1000000000 + 1)) + 1000000000;
  };

  /**
   * Display the calculated prices for elements
   *
   * @param {number} calculatedPrice The calculated price
   * @param {array} elementArr Array of elements to update
   * @param {undefined|object} variant Variant to use for the price
   */
  displayCalculatedPrice = (calculatedPrice, elementArr, variant) => {
    let self = this;

    elementArr.forEach((e) => {
      if (document.getElementById(e) !== null) {
        let price = self.getFormattedPrice(calculatedPrice);
        if (price) {
          document.getElementById(e).innerText = price;
        } else {
          document.getElementById(e).innerText = '';
        }
        if (typeof variant !== 'undefined' || variant !== null) {
          document.getElementById(e).dataset.product_id = variant.product;
          document.getElementById(e).dataset.variant_id = variant.variant;
        }
      }
    });
  };

  /**
   * Find the closest wall variant based on price and the type of wall
   *
   * @param {number} target Price
   * @param {string} type Type
   * @returns {number} Price
   */
  findClosestWallPrice = (target, type) => {
    const self = this;

    let result = self.findClosestVariantByType(target, type);

    return result.price;
  };

  resetProducts = () => {
    let self = this;
    self.resetCheckboxes();

    // TODO: threeDViever (1583th line) is needed for this part
    /* lengthSlider.value = 3060
        widthSlider.value = 3000
        heightSlider.value = 2000
        valueInput1.value = "3000mm"
        valueInput2.value = "3060mm"
        valueInput3.value = "2000mm" */
  };

  resetCheckboxes = () => {
    const elementArr = [
      '#pxNoBackWall',
      '#pxfreestand',
      '#pxNoFrontWall',
      '#pxNoRightWall',
      '#pxNoLeftWall',
      '.variant-input',
    ];

    elementArr.forEach((e) => {
      const element = document.querySelector(e);
      if (element) {
        element.checked = true;
      }
    });
    document.getElementById('ledToggle').checked = false;
    document.getElementById('ledToggleRGB').checked = false;
    document.querySelectorAll('.free-accessory-toggle').forEach((toggle) => {
      toggle.checked = true;
    });
  };

  setDiscount = () => {
    let self = this;
    if (self.FORMULA.discount_percent == 0) {
      return;
    }

    let discountText = self.FORMULA.discount_text;
    discountText = discountText.replace(
      '{discount_percent}',
      self.FORMULA.discount_percent.toFixed(0)
    );
    const discountLabel = discountText;

    if (!!document.getElementById('discount')) {
      document.getElementById('discount').innerText = discountLabel;
      document.getElementById('discountC').innerText = discountLabel;
    }
  };

  /**
   * Update the color on the frontend
   *
   * @param {string} color
   */
  updateSelectedColor = (color) => {
    document.getElementById('selectedColor').innerText = color;
    // Additional logic if needed...
  };

  /**
   * Update the slider and input values
   *
   * @param {HTMLElement} slider Slider element
   * @param {HTMLElement} input Input element
   */
  updateSliderAndInput = (slider, input, val = 0) => {
    let self = this;
    let min = parseInt(slider.attributes.min.value);
    let max = parseInt(slider.attributes.max.value);
    const value = val ? val : parseInt(input.value.replace(/\D/g, '')); // Remove non-numeric characters
    if (value >= min && value <= max) {
      input.value = value + 'mm';
      slider.value = value;
    } else if (value < min) {
      input.value = min + 'mm';
      slider.value = min;
    } else if (value > max) {
      input.value = max + 'mm';
      slider.value = max;
    }
  };

  /**
   * Replace strings in a text
   *
   * @param {string} text
   * @param {string} find
   * @param {string} replace
   * @returns {string} Returns the modified text
   */
  replaceString = (text, find, replace) => {
    return text.replace(find, replace);
  };

  /**
   * Get the nearest multiple for the current value
   *
   * @param {number} value Current value
   * @param {number} min Minimum value
   * @param {number} max Maximum value
   * @param {number} base Base value to use as difference
   * @returns {number}
   */
  getNearestMultiple = (value, min, max, base = 150) => {
    // Adjust the result to the nearest multiple of 150 within the range
    let nearestMultiple = Math.round((value - min) / base) * base + min;

    // Ensure the nearest multiple is within the range of 1580 to 7110
    return Math.max(min, Math.min(nearestMultiple, max));
  };

  /**
   * Set the input value and update the 3D model viewer
   *
   * @param {HTMLElement} input
   * @param {HTMLElement} viewer
   * @param {string} control
   * @param {number} value
   * @param {number} min
   * @param {number} max
   */
  setSliderAndInput = (input, viewer, control, value, min, max) => {
    if (value >= min && value <= max) {
      switch (control) {
        case 'width':
          viewer.pxWidthControl(value);
          break;
        case 'length':
          viewer.pxLengthControl(value);
          break;
        case 'height':
          viewer.pxHeightControl(value);
          break;
      }
      input.value = value + 'mm';
    } else if (value < min) {
      switch (control) {
        case 'width':
          viewer.pxWidthControl(min);
          break;
        case 'length':
          viewer.pxLengthControl(min);
          break;
        case 'height':
          viewer.pxHeightControl(min);
          break;
      }
      input.value = min + 'mm';
    } else if (value > max) {
      switch (control) {
        case 'width':
          viewer.pxWidthControl(max);
          break;
        case 'length':
          viewer.pxLengthControl(max);
          break;
        case 'height':
          viewer.pxHeightControl(max);
          break;
      }
      input.value = max + 'mm';
    }
  };

  setColorBasedOnVariant = (variantColor, viewer) => {
    let color = 'Antracite';
    switch (variantColor) {
      case 'Weiß':
        color = 'White';
        break;
      case 'Anthrazit Pergola mit Weißem Lamellendach':
        color = 'Antracite-White';
        break;
      case 'Schwarz':
        color = 'Black';
        break;
      case 'Schwarze Pergola mit Weißem Lamellendach':
        color = 'Antracite-Black';
        break;
      case 'Anthrazit':
        color = 'Antracite';
        break;
    }

    if (color) {
      viewer.pxColor(color);
    }
  };

  setSize = (
    value,
    viewer,
    lengthSlider,
    widthSlider,
    valueInput1,
    valueInput2,
    slider1,
    slider2
  ) => {
    let size = value.replace('m', '').split('x');
    size[0] = parseInt(size[0]) * 1000;
    size[1] = parseInt(size[1]) * 1000;
    lengthSlider.value = size[0];
    valueInput1.value = size[0] + 'mm';

    let min = parseInt(slider2.attributes.min.value);
    let max = parseInt(slider2.attributes.max.value);
    let step = parseInt(slider2.attributes.step.value);

    // Adjust the result to the nearest multiple of 150 within the range
    let nearestMultiple = pergolaProduct.getNearestMultiple(size[1], min, max, step);
    widthSlider.value = nearestMultiple;
    valueInput2.value = nearestMultiple + 'mm';

    window.setTimeout(function () {
      pergolaProduct.updateSliderAndInput(slider1, valueInput1);
      pergolaProduct.updateSliderAndInput(slider2, valueInput2);
      viewer.pxWidthControl(size[0]);
      viewer.pxLengthControl(nearestMultiple);
    }, 150);
  };

  toggleSideOption = (input, enable) => {
    let self = this;
    if (!enable) {
      input.disabled = true;
      input.closest('.box-size').classList.add('disabled');
    } else {
      input.disabled = false;
      input.closest('.box-size').classList.remove('disabled');
    }
  };

  setBackWall = (value, viewer) => {
    let self = this;
    self.setStructureVariant();
    if (value === self.translations.wall_mounting) {
      viewer.pxBackWall('None');
      viewer.pxWallAttachmentHandle('WM');
      for (let input of document.querySelectorAll(
        'input[name="properties[' + self.translations.back_side + ']"]'
      )) {
        if (input.value.trim() !== self.translations.none) {
          self.toggleSideOption(input, 0);
        } else {
          input.checked = true;
        }
      }
    } else {
      viewer.pxWallAttachmentHandle('FS');
      viewer.pxBackWall('FS');
      for (let input of document.querySelectorAll(
        'input[name="properties[' + self.translations.back_side + ']"]'
      )) {
        self.toggleSideOption(input, 1);
      }
    }
    self.calculateSizes();
  };

  /**
   * Set structure variant for feet calculation
   *
   * @param {boolean} value
   */
  setStructureVariant = () => {
    let self = this;
    let value = null;
    if (self.mainProduct) {
      value = document.querySelector(
        'input[name="' + self.translations.mounting + '"]:checked'
      ).value;
    } else {
      value = document.querySelector(
        'input[name="properties[' + self.translations.mounting + ']"]:checked'
      ).value;
    }

    self.structure_variant = value === self.translations.wall_mounting;
  };

  getColumnFeet = (size, mounted = false, hidden = false) => {
    let feet = 2;

    if (mounted) {
      feet = feet - 1;
    }
    if (size >= 5060) {
      feet = feet + 1;
    }
    if (hidden) {
      feet = feet - 2;
    }

    return feet;
  };
  updateCartCountElements(itemCount) {
    const cartItemCountElements = document.querySelectorAll('[data-item-count]');

    cartItemCountElements.forEach((element) => {
      element.setAttribute('data-item-count', itemCount);
    });
  }

  async getCart() {
    let self = this;
    try {
      const response = await resilientFetch(window.routes.cart_url);
      if (!response.ok) throw new Error();

      const cart = await response.json();
      if (!cart) throw new Error();

      this.updateCartCountElements(cart.item_count);

      // Generic event - Used to update data like cart count etc..
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            cart,
          },
        })
      );

      cart.items.forEach(item => {
        if (item.properties && item.properties[self.translations.market]) {
          delete item.properties[self.translations.market];
        }
      });

      return cart;
    } catch (error) {
      console.error(error.message);

      return {};
    }
  }
  generateRandomId() {
    return 'id-' + Date.now().toString(36) + Math.random().toString(36).substring(2, 10);
  }

  /**
   * Convert millimeters to display format based on unit setting
   * Uses the same logic as convertMMtoFeetInchesAndFraction from utils.js
   * 
   * @param {number} mm - Value in millimeters
   * @param {string} type - Type of measurement: 'configurator' or 'structure'
   * @returns {string} - Formatted value with unit
   */
  formatSizeValue(value, type = 'configurator') {
    let self = this;
    
    if (self.pergolaUnit !== 'feet_and_inches') {
      return value.toString();
    }

    const INCHES_TO_MM = 25.4;
    const INCHES_PER_FOOT = 12;
    const isStructureDimension = type === 'structure';

    let totalInches = value / INCHES_TO_MM;
    let fractionalIn32nds;

    if (isStructureDimension) {
      // For structure dimensions: calculate fraction in 32nds and handle overflow
      const allInches = Math.floor(totalInches);
      const fractionalInches = totalInches - allInches;
      fractionalIn32nds = Math.round(fractionalInches * 32);

      // Handle 32/32 overflow (becomes 1 whole inch)
      if (fractionalIn32nds === 32) {
        totalInches = allInches + 1;
        fractionalIn32nds = 0;
      } else {
        totalInches = allInches;
      }
    } else {
      // For configurator: round to nearest whole inch
      totalInches = Math.round(totalInches);
      fractionalIn32nds = 0;
    }

    // Convert total inches to feet and inches
    const feet = Math.floor(totalInches / INCHES_PER_FOOT);
    const wholeInches = totalInches % INCHES_PER_FOOT;

    let displayString;
    if (isStructureDimension && fractionalIn32nds > 0) {
      // Simplify the fraction
      const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
      const divisor = gcd(fractionalIn32nds, 32);
      const numerator = fractionalIn32nds / divisor;
      const denominator = 32 / divisor;

      if (feet === 0 && wholeInches === 0) {
        displayString = `${numerator}/${denominator}"`;
      } else if (feet === 0) {
        displayString = `${wholeInches} ${numerator}/${denominator}"`;
      } else if (wholeInches === 0) {
        displayString = `${feet}' ${numerator}/${denominator}"`;
      } else {
        displayString = `${feet}' ${wholeInches} ${numerator}/${denominator}"`;
      }
    } else if (feet === 0) {
      // No fraction, no feet
      displayString = `${wholeInches}"`;
    } else {
      // No fraction, has feet
      displayString = `${feet}' ${wholeInches}"`;
    }

    return displayString;
  }
}
